import {
  DataSourceTypeDescription,
  GENERIC_SQL_DATA_SOURCE_TYPE,
  JDBC_DATA_SOURCE_FAMILY,
  UNKNOWN_DATA_SOURCE_FAMILY,
  UNKNOWN_DATA_SOURCE_TYPE,
  defaultTypeDescription,
} from 'src/constants/dataSourceTypeDescription';

/**
 * Determines which family a data source belongs to based on whether it has all
 * of the required options for that family.
 *
 * @param options Data source options retrieved from the data_sources/options endpoint
 * @param typeDescription Type description retrieved from the data_source_type_description endpoint
 */
const getDataSourceFamilyFromOptions = (
  options: Record<string, any>,
  typeDescription: DataSourceTypeDescription
) => {
  return (
    Object.keys(typeDescription.DATA_SOURCE_FAMILIES).find((type) => {
      const requiredOptions = Object.entries(
        typeDescription.DATA_SOURCE_FAMILIES[type].DATA_SOURCE_OPTIONS
      )
        .filter(([_, optionDetails]) => optionDetails.required)
        .map(([option]) => option);
      return requiredOptions.every((option) => options[option] !== undefined);
    }) || UNKNOWN_DATA_SOURCE_FAMILY
  );
};

/**
 * Determines the type of a data source based on its family, and driver if it's
 * a JDBC data source (for non-JDBC sources, there is currently only one type
 * supported per family)
 *
 * @param options Data source options retrieved from the data_sources/options endpoint
 * @param typeDescription Type description retrieved from the data_source_type_description endpoint
 */
export const getDataSourceTypeFromOptions = (
  options: Record<string, any>,
  typeDescription: DataSourceTypeDescription = defaultTypeDescription
) => {
  const family = getDataSourceFamilyFromOptions(options, typeDescription);
  if (family === JDBC_DATA_SOURCE_FAMILY) {
    // Match JDBC sources by their driver
    if (!options['jdbc.driver']) {
      return GENERIC_SQL_DATA_SOURCE_TYPE;
    }
    return (
      Object.keys(typeDescription.DATA_SOURCE_TYPES).find(
        (type) =>
          options['jdbc.driver'] ===
          typeDescription.DATA_SOURCE_TYPES[type].DATA_SOURCE_OPTIONS?.[
            'jdbc.driver'
          ]?.default
      ) || GENERIC_SQL_DATA_SOURCE_TYPE
    );
  }
  // Pick the first data source from the family
  return (
    Object.keys(typeDescription.DATA_SOURCE_TYPES).find(
      (type) =>
        family === typeDescription.DATA_SOURCE_TYPES[type].dataSourceFamily
    ) || UNKNOWN_DATA_SOURCE_TYPE
  );
};
